import { useEffect, useState } from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import zod from 'zod';

import { useOktaUser } from '~/modules/settings/api/queries/okta-user';

const emailSchema = zod.email();

/**
 * This hook is used to fetch and manage Okta user data based on the email provided in the form.
 *
 */
export const useOktaUserData = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  isCreateMode = true,
) => {
  const [readOnlyFields, setReadOnlyFields] = useState<string[]>([]);

  const { watch, setValue } = form;

  // Just shortcut for the type to make the code easier to read
  type FormKeyType = Path<TFieldValues>;
  type FormPathValueType = PathValue<TFieldValues, Path<TFieldValues>>;

  const email = watch('email' as FormKeyType);

  const { oktaUser } = useOktaUser(email, {
    enabled:
      isCreateMode && Boolean(email) && emailSchema.safeParse(email).success,
  });

  useEffect(() => {
    if (email && emailSchema.safeParse(email).success && oktaUser?.profile) {
      const newReadOnlyFields = [] as string[];

      if (oktaUser.profile.firstName) {
        setValue(
          'firstName' as FormKeyType,
          oktaUser.profile.firstName as FormPathValueType,
          {
            shouldValidate: true,
          },
        );
        newReadOnlyFields.push('firstName');
      }

      if (oktaUser.profile.lastName) {
        setValue(
          'lastName' as FormKeyType,
          oktaUser.profile.lastName as FormPathValueType,
          {
            shouldValidate: true,
          },
        );
        newReadOnlyFields.push('lastName');
      }

      if (oktaUser.profile.mobilePhone) {
        setValue(
          'phone' as FormKeyType,
          oktaUser.profile.mobilePhone as FormPathValueType,
          {
            shouldValidate: true,
          },
        );
        newReadOnlyFields.push('phone');
      }

      setReadOnlyFields(newReadOnlyFields);
    } else {
      // If email is invalid or empty, reset the disabled fields
      setReadOnlyFields([]);
    }
  }, [email, oktaUser?.profile, setValue]);

  return {
    readOnlyFields,
  };
};
